"use client"

import { useState } from "react"
import Link from "next/link"
import { communautePostCategories, communautePosts as initialPosts, currentUser } from "@/lib/mockData"
import { useUnlock } from "@/lib/useUnlock"

export default function Communaute() {
  const { unlocked } = useUnlock()
  const [posts, setPosts] = useState(initialPosts)
  const [draft, setDraft] = useState("")
  const [location, setLocation] = useState("")
  const [category, setCategory] = useState<(typeof communautePostCategories)[number]>(communautePostCategories[0])

  const canPublish = draft.trim() !== "" && location.trim() !== ""

  const handlePublish = () => {
    if (!canPublish) return
    setPosts((current) => [
      {
        id: `c${Date.now()}`,
        author: currentUser.name,
        location: location.trim(),
        timeAgo: "à l'instant",
        type: category,
        content: draft.trim(),
        likes: 0,
      },
      ...current,
    ])
    setDraft("")
    setLocation("")
    setCategory(communautePostCategories[0])
  }

  return (
    <div className="max-w-3xl mx-auto px-5 py-10 space-y-6">
      <div>
        <h1 className="text-2xl">La communauté</h1>
        <p className="text-ink/60 text-sm mt-1">
          Les astuces des membres : horaires malins, adresses testées, bons plans du Kent et de la Côte d&apos;Opale.
        </p>
      </div>

      {unlocked ? (
        <div className="bg-white/70 border border-abyss/10 rounded-xl p-4 space-y-3">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Partagez une astuce, un bon plan, une découverte..."
            className="w-full resize-none bg-transparent text-sm outline-none placeholder:text-ink/40"
            rows={2}
          />
          <div className="flex gap-3 flex-wrap">
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Lieu (ex : Calais, Canterbury...)"
              className="flex-1 min-w-[180px] bg-sand border border-abyss/10 rounded-full px-4 py-1.5 text-sm outline-none placeholder:text-ink/40"
            />
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as (typeof communautePostCategories)[number])}
              className="bg-sand border border-abyss/10 rounded-full px-4 py-1.5 text-sm outline-none"
            >
              {communautePostCategories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div className="flex justify-end">
            <button
              onClick={handlePublish}
              disabled={!canPublish}
              className={`text-sm px-4 py-1.5 rounded-full font-medium transition-colors ${
                canPublish
                  ? "bg-flare text-abyss hover:bg-flare-dark hover:text-foam"
                  : "bg-abyss/10 text-ink/40 cursor-not-allowed"
              }`}
            >
              Publier
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-flare/15 border border-flare/40 rounded-xl p-6 flex items-center justify-between gap-4">
          <div>
            <h2 className="text-lg">Publication réservée aux membres</h2>
            <p className="text-sm text-ink/70 mt-1">
              Donnez votre avis sur votre traversée pour publier et commenter.
            </p>
          </div>
          <Link
            href="/questionnaire"
            className="shrink-0 bg-flare text-abyss px-5 py-2.5 rounded-full font-medium hover:bg-flare-dark hover:text-foam transition-colors"
          >
            Rejoindre
          </Link>
        </div>
      )}

      <div className="space-y-4">
        {posts.map((p) => (
          <article key={p.id} className="bg-white/70 border border-abyss/10 rounded-xl p-5">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-wide text-channel font-medium">{p.type}</span>
              <span className="text-xs text-ink/40">{p.timeAgo}</span>
            </div>
            <p className="text-sm text-ink/80 mt-2">{p.content}</p>
            <div className="flex items-center gap-4 mt-4 text-sm text-ink/50">
              <span>
                {p.author} · {p.location}
              </span>
              <span className="ml-auto">♥ {p.likes}</span>
            </div>
          </article>
        ))}
      </div>
    </div>
  )
}
